import { Form } from "antd";
import { Autocomplete, Text } from "components/UI";
import useSuggestion from "hooks/useSuggestion";
import { isEmpty } from "lodash-es";
import { useInfiniteContactSuggestion } from "queries/conversation";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cleanBlankValue } from "utils/helper";

const ContactSelector = ({
  autocompleteProps,
  name = "contact_id",
  defaultValue,
  selectedContact,
  // Diisi id channel inhouse yang sedang dibuka supaya suggestion kontak
  // dibatasi ke channel tsb. Kosong = tanpa filter (channel WhatsApp, dll).
  inhouseChannelId,
  ...props
}) => {
  const {
    debouncedSearch,
    onDropdownVisible,
    getPopupContainer,
    onPopUpScroll,
    containerId,
    onSearch,
    popUpClassName,
    open,
  } = useSuggestion({
    name,
    module: "contact",
    // onDropdownVisibleCallback: dropdownVisibleChangeHandler,
  });
  const { t } = useTranslation();

  const params = useMemo(() => {
    const newParams = {
      search: debouncedSearch,
      inhouse_channel_id: inhouseChannelId,
    };

    return cleanBlankValue(newParams);
  }, [debouncedSearch, inhouseChannelId]);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteContactSuggestion(params);

  const formattedData = useMemo(() => {
    let newData = data?.pages.flatMap((page) => page.data) || [];
    if (!newData.some((data) => data.id === defaultValue?.id)) {
      newData.push(defaultValue);
    }
    if (selectedContact) {
      // Prefill dari chat (TicketSection) sudah menggabungkan perusahaan ke
      // `name`; pakai `name_only` supaya perusahaan tidak tampil dobel.
      const label = `${selectedContact.name_only ?? selectedContact.name}${
        selectedContact.company ? ` - ${selectedContact.company}` : ""
      }`;
      const merged = {
        ...selectedContact,
        value: selectedContact.id,
        label,
        formatted: label,
      };
      const idx = newData.findIndex(
        (item) => item?.id === selectedContact.id
      );
      if (idx === -1) {
        newData.unshift(merged);
      } else {
        // Utamakan data selectedContact (lebih baru, mis. setelah edit kontak)
        // ketimbang entri lama yang masih ter-cache di daftar suggestion.
        newData[idx] = { ...newData[idx], ...merged };
      }
    }
    // Kontak yang sedang terpilih tetap dipertahankan walau tanpa nomor HP,
    // kalau tidak Select hanya menampilkan id mentah (mis. kontak inhouse
    // prefill dari chat yang dibuka di form ticket/jadwal).
    const keepIds = [defaultValue?.id, selectedContact?.id].filter(
      (id) => id != null
    );
    return newData
      // Kontak inhouse tidak punya nomor HP, jadi filter phone hanya untuk
      // channel non-inhouse (WhatsApp, dll).
      .filter(
        (row) =>
          row &&
          (inhouseChannelId ||
            !isEmpty(row.phone) ||
            keepIds.includes(row.id))
      )
      .map((row) => {
        return {
          ...row,
          value: row?.id,
          label: row?.formatted,
        };
      });
  }, [data?.pages, defaultValue, selectedContact, inhouseChannelId]);

  const popUpScrollHandler = useCallback(
    (e) => {
      onPopUpScroll(e, { hasNextPage, isFetchingNextPage, fetchNextPage });
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, onPopUpScroll]
  );

  const optionRender = useCallback((option) => {
    return (
      <div className="flex flex-col flex-wrap">
        <Text ellipsis>{option.data.formatted}</Text>
        <div className="flex flex-row items-center justify-between">
          {!isEmpty(option.data.email) && (
            <Text type="secondary">{option.data.email}</Text>
          )}
          <Text type="secondary">{option.data.phone}</Text>
        </div>
      </div>
    );
  }, []);
  return (
    <Form.Item id={containerId} name={name} {...props}>
      <Autocomplete
        placeholder={t("select_contact")}
        options={formattedData}
        allowClear
        onSearch={onSearch}
        infiniteScroll
        onPopupScroll={popUpScrollHandler}
        // fieldNames={{ label: "formatted", value: "id" }}
        onDropdownVisibleChange={onDropdownVisible}
        loading={isLoading}
        loadingNextPage={isLoading || isFetchingNextPage}
        getPopupContainer={getPopupContainer}
        popupClassName={popUpClassName}
        open={open}
        optionRender={optionRender}
        optionLabelProp="label"
        {...autocompleteProps}
      />
    </Form.Item>
  );
};

export default ContactSelector;
